import React from 'react';

import { createPortal } from 'react-dom';

import AkFeatureGates from '@atlaskit/feature-gate-js-client';

import { DEFAULT_HEARTBEAT_ENDPOINT, DEFAULT_IDLE_TIME } from '../../constants';
import { useHeartbeatOnInterval } from '../../controllers/use-heartbeat-on-interval';
import { createStorageAPI } from '../../utils/typed-storage';

import { HeartbeatIntlProvider } from './common/utils/locale';
import BannerContent from './components/BannerContent';
import HeartbeatErrorBoundary from './components/HeartbeatErrorBoundary';
import { useBannerContainer } from './hooks/useBannerContainer';
import { useBannerState } from './hooks/useBannerState';
import { useCrossTabSync } from './hooks/useCrossTabSync';
import { type HeartbeatProps } from './types';

/**
 * Internal Heartbeat component implementation without IntlProvider.
 * Contains all the core heartbeat logic and rendering.
 * Following admin-hub pattern: separate impl from provider wrapper.
 */
const HeartbeatImpl = ({
	sessionExpiryDate,
	storage = window.localStorage,
}: {
	sessionExpiryDate: Date | null;
	storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}): JSX.Element | null => {
	const sessionExpiryTimestamp = sessionExpiryDate?.getTime() ?? null;

	// Wrap storage with typed API - evaluated lazily to support test mocks
	const storageAPI = createStorageAPI(storage);

	// Cross-tab synchronization (centralizes all storage event listening)
	const { lastSessionExpiry } = useCrossTabSync(sessionExpiryTimestamp, storageAPI);

	// Banner state management (listens to storage events for dismissal changes)
	const { timeRemainingMs, isVisible, canHide, handleDismiss, handleReauthClick } = useBannerState(
		sessionExpiryTimestamp,
		lastSessionExpiry,
		storageAPI,
	);

	// DOM container management
	const { bannerContainer } = useBannerContainer(isVisible);

	if (!isVisible || !bannerContainer) {
		return null;
	}

	// Render via portal into injected container
	return createPortal(
		<BannerContent
			timeRemainingMs={timeRemainingMs}
			canDismiss={canHide}
			onDismiss={() => handleDismiss()}
			onReauthenticate={handleReauthClick}
		/>,
		bannerContainer,
	);
};

/**
 * Session expiration banner component that uses DOM injection
 * to display session warnings without covering page content.
 *
 * Features:
 * - DOM positioning as first child of body with portal rendering
 * - Cross-tab session state synchronization via storage events
 * - Optimized re-render performance with custom hooks
 * - Comprehensive error handling and cleanup
 * - Error boundary protection for graceful failure handling
 * - Feature flag controlled: service always runs, UI only when enabled
 */
/**
 * Service component that always runs heartbeat monitoring, wrapped in error boundary
 */
const HeartbeatService = (props: HeartbeatProps): JSX.Element => {
	// Always run heartbeat service for session monitoring
	const { sessionExpiryTimestamp } = useHeartbeatOnInterval(
		props.endpoint || DEFAULT_HEARTBEAT_ENDPOINT,
		props.idleTime || DEFAULT_IDLE_TIME,
		props.onAuthenticationFailed,
		props.onBadRequest,
	);

	const sessionExpiryDate = sessionExpiryTimestamp ?? null;

	// Check feature flag to control banner UI rollout
	const isBannerEnabled = AkFeatureGates.checkGate('heartbeat_session_expiry_banner');

	return (
		<>
			{/* Only show UI when feature flag is enabled */}
			{isBannerEnabled && (
				<HeartbeatIntlProvider>
					<HeartbeatErrorBoundary>
						<HeartbeatImpl sessionExpiryDate={sessionExpiryDate} storage={props.storage} />
					</HeartbeatErrorBoundary>
				</HeartbeatIntlProvider>
			)}
		</>
	);
};

const Heartbeat = (props: HeartbeatProps): JSX.Element => {
	return (
		<HeartbeatErrorBoundary>
			<HeartbeatService {...props} />
		</HeartbeatErrorBoundary>
	);
};

export default Heartbeat;
